import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaEye, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import QuestionSectionModal from './QuestionSectionModal';
import QuestionEditModal from './QuestionEditModal';
import QuestionPreview from './QuestionPreview';
import './QuestionManagement.css';

const QuestionManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('1040');
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [editMode, setEditMode] = useState('create');

  const formTypes = ['1040', '1065', '1120-S', '1120'];

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch questions');

      const data = await response.json();
      setQuestions(data.data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSection = () => {
    setSelectedSection(null);
    setEditMode('create');
    setShowSectionModal(true);
  };

  const handleEditSection = (section) => {
    setSelectedSection(section);
    setEditMode('edit');
    setShowSectionModal(true);
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${sectionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete section');

      await fetchQuestions();
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleToggleSection = async (sectionId, currentStatus) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${sectionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (!response.ok) throw new Error('Failed to toggle section');

      await fetchQuestions();
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handlePreview = (section) => {
    setSelectedSection(section);
    setShowPreview(true);
  };

  const handleSectionSave = async (sectionData) => {
    const token = localStorage.getItem('adminToken');
    try {
      const url = editMode === 'create' 
        ? `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section`
        : `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${selectedSection._id}`;
      
      const method = editMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(sectionData)
      });

      if (!response.ok) throw new Error(`Failed to ${editMode} section`);

      await fetchQuestions();
      setShowSectionModal(false);
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleInitializeQuestions = async () => {
    if (!window.confirm('This will initialize default questions. Continue?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/initialize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to initialize questions');

      await fetchQuestions();
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const getFormQuestions = (formType) => {
    return questions.filter(q => q.formType === formType);
  };

  const renderQuestionSection = (section) => (
    <Card key={section._id} className="mb-3 question-section-card">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">{section.sectionTitle}</h5>
          <small className="text-muted">
            {section.questions.length} questions • Order: {section.sectionOrder}
          </small>
        </div>
        <div className="section-actions">
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => handlePreview(section)}
            className="me-2"
          >
            <FaEye /> Preview
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEditSection(section)}
            className="me-2"
          >
            <FaEdit /> Edit
          </Button>
          <Button
            variant={section.isActive ? "outline-success" : "outline-secondary"}
            size="sm"
            onClick={() => handleToggleSection(section._id, section.isActive)}
            className="me-2"
          >
            {section.isActive ? <FaToggleOn /> : <FaToggleOff />}
            {section.isActive ? ' Active' : ' Inactive'}
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDeleteSection(section._id)}
          >
            <FaTrash /> Delete
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="questions-preview">
          {section.questions.slice(0, 3).map((question, index) => (
            <div key={index} className="question-preview-item">
              <small className="text-muted">Q{index + 1}:</small>
              <span className="ms-2">{question.text}</span>
            </div>
          ))}
          {section.questions.length > 3 && (
            <small className="text-muted">... and {section.questions.length - 3} more questions</small>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading questions...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="question-management-container">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Question Management</h2>
            <div>
              <Button
                variant="success"
                onClick={handleCreateSection}
                className="me-2"
              >
                <FaPlus /> Add New Section
              </Button>
              <Button
                variant="info"
                onClick={handleInitializeQuestions}
              >
                Initialize Default Questions
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Card>
            <Card.Body>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
              >
                {formTypes.map(formType => (
                  <Tab
                    key={formType}
                    eventKey={formType}
                    title={`Form ${formType}`}
                  >
                    <div className="form-questions-container">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>Form {formType} Questions</h4>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleCreateSection}
                        >
                          <FaPlus /> Add Section for {formType}
                        </Button>
                      </div>
                      
                      {getFormQuestions(formType).length === 0 ? (
                        <Alert variant="info">
                          No questions found for Form {formType}. Click "Add Section" to create your first section.
                        </Alert>
                      ) : (
                        <div className="questions-list">
                          {getFormQuestions(formType)
                            .sort((a, b) => a.sectionOrder - b.sectionOrder)
                            .map(renderQuestionSection)}
                        </div>
                      )}
                    </div>
                  </Tab>
                ))}
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <QuestionSectionModal
        show={showSectionModal}
        onHide={() => setShowSectionModal(false)}
        onSave={handleSectionSave}
        section={selectedSection}
        mode={editMode}
        activeFormType={activeTab}
      />

      <QuestionEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        section={selectedSection}
        onSave={fetchQuestions}
      />

      <QuestionPreview
        show={showPreview}
        onHide={() => setShowPreview(false)}
        section={selectedSection}
      />
    </Container>
  );
};

export default QuestionManagement;